/**
 * Tracks whether the user has previously signed up / logged in.
 * Used to send returning devices to Log in and new devices to Sign up
 * on every logged-out auth redirect.
 *
 * Stored in localStorage and a first-party cookie so client navigations
 * and later visits can both see it. SSR has no localStorage; the cookie
 * is available on subsequent browser requests (client hydrate uses
 * localStorage / document.cookie).
 */

export const HAS_ACCOUNT_STORAGE_KEY = 'soulwise-has-account';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2;

const canUseLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const canUseDocumentCookie = () =>
  typeof document !== 'undefined' && typeof document.cookie === 'string';

const readAccountCookie = () => {
  if (!canUseDocumentCookie()) {
    return false;
  }
  return document.cookie.split(';').some(part => part.trim() === `${HAS_ACCOUNT_STORAGE_KEY}=1`);
};

const writeAccountCookie = () => {
  if (!canUseDocumentCookie()) {
    return;
  }
  document.cookie = `${HAS_ACCOUNT_STORAGE_KEY}=1; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
};

/**
 * Mark that the user has an account (called on successful login, signup, or confirm).
 */
export const markUserHasAccount = () => {
  if (canUseLocalStorage()) {
    try {
      window.localStorage.setItem(HAS_ACCOUNT_STORAGE_KEY, '1');
    } catch {
      // ignore quota / private mode
    }
  }
  writeAccountCookie();
};

/**
 * Check if the user is a returning user (has signed up or logged in before).
 * @returns {boolean}
 */
export const hasUserAccount = () => {
  if (canUseLocalStorage()) {
    try {
      if (window.localStorage.getItem(HAS_ACCOUNT_STORAGE_KEY) === '1') {
        return true;
      }
    } catch {
      // fall through to cookie
    }
  }
  return readAccountCookie();
};
